import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bidSubmissionApi, CreateBidSubmissionDto, UpdateBidSubmissionDto } from '../../../api/bidSubmissions';
import { notifications } from '@mantine/notifications';

// Hook to get bid submissions for a tender
export function useBidSubmissions(tenderId: string) {
  return useQuery({
    queryKey: ['bid-submissions', tenderId],
    queryFn: () => bidSubmissionApi.listBidSubmissions(tenderId),
    enabled: !!tenderId,
  });
}

// Hook to create a bid submission
export function useCreateBidSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: CreateBidSubmissionDto }) =>
      bidSubmissionApi.createBidSubmission(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      // Invalidate and refetch bid submissions for this tender
      queryClient.invalidateQueries({ queryKey: ['bid-submissions', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bid submission created successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error creating bid submission:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create bid submission. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to update a bid submission
export function useUpdateBidSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, submissionId, data }: { tenderId: string; submissionId: string; data: UpdateBidSubmissionDto }) =>
      bidSubmissionApi.updateBidSubmission(tenderId, submissionId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['bid-submissions', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bid submission updated successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error updating bid submission:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update bid submission. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to remove a bid submission
export function useRemoveBidSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, submissionId }: { tenderId: string; submissionId: string }) =>
      bidSubmissionApi.removeBidSubmission(tenderId, submissionId),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['bid-submissions', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bid submission removed successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error removing bid submission:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to remove bid submission. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to get bid submission details
export function useBidSubmissionDetails(tenderId: string, submissionId: string) {
  return useQuery({
    queryKey: ['bid-submission-details', tenderId, submissionId],
    queryFn: () => bidSubmissionApi.getBidSubmissionById(tenderId, submissionId),
    enabled: !!tenderId && !!submissionId,
  });
}

// Hook to search existing bidders
export function useSearchExistingBidders(query: string, enabled: boolean = false) {
  return useQuery({
    queryKey: ['search-bidders', query],
    queryFn: () => bidSubmissionApi.searchExistingBidders(query),
    enabled: enabled && !!query.trim(),
  });
}
